import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import VisibilityIcon from '@material-ui/icons/Visibility';

import getStudiesUnderReview from 'api/studyReview/getStudiesUnderReview';
import { approveStudy, rejectStudy } from 'redux/reducers/studyApproval/actions';
import { enqueueGlobalUserMessageState } from 'redux/reducers/globalUserMessaging';
import { format as dateFormat } from 'date-fns';
import { getDateLocale } from 'translations/i18n';
import { parseStudyEntries } from './adminHelpers';

const StyledTableContainer = styled(TableContainer)`
    margin-top: 1em;
`;

const ActionButton = styled(Button)`
    margin: 0 4px;
    text-transform: none;
    min-width: 100px;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 4em 0;
    color: rgba(0, 0, 0, 0.54);
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 4em 0;
`;

const StudyTitle = styled.span`
    font-weight: 600;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

export default function StudyApprovalsTab() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const saving = useSelector(state => state.serverCommunication.saving);

    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, study: null });

    const fetchStudies = useCallback(() => {
        setLoading(true);
        getStudiesUnderReview()
            .then(({ data }) => {
                setStudies(parseStudyEntries(data, t));
            })
            .catch(() => {
                dispatch(enqueueGlobalUserMessageState('error', 'error.generic'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, t]);

    useEffect(() => {
        fetchStudies();
    }, [fetchStudies]);

    function handleConfirm(action, study) {
        setConfirmDialog({ open: true, action, study });
    }

    function handleClose() {
        setConfirmDialog({ open: false, action: null, study: null });
    }

    function handleAction() {
        const { action, study } = confirmDialog;
        handleClose();
        if (action === 'approve') {
            dispatch(approveStudy(study.id)).then(() => fetchStudies());
        } else if (action === 'reject') {
            dispatch(rejectStudy(study.id, {})).then(() => fetchStudies());
        }
    }

    function viewStudy(studyId) {
        history.push(`/review?study=${studyId}`);
    }

    if (loading) {
        return (
            <LoadingContainer>
                <CircularProgress color="primary" />
            </LoadingContainer>
        );
    }

    if (studies.length === 0) {
        return (
            <EmptyState>
                <Typography variant="h6" color="textSecondary">
                    {t('admin_noStudies')}
                </Typography>
            </EmptyState>
        );
    }

    return (
        <React.Fragment>
            <StyledTableContainer component={Paper} elevation={1}>
                <Table aria-label={t('admin_pageTitle')}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('admin_studyName')}</TableCell>
                            <TableCell>{t('admin_submittedDate')}</TableCell>
                            <TableCell align="right">{t('admin_actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {studies.map(study => (
                            <TableRow key={study.id}>
                                <TableCell>
                                    <StudyTitle
                                        role="link"
                                        tabIndex={0}
                                        onClick={() => viewStudy(study.id)}
                                        onKeyDown={e => e.key === 'Enter' && viewStudy(study.id)}
                                    >
                                        {study.title}
                                    </StudyTitle>
                                </TableCell>
                                <TableCell>
                                    {study.date
                                        ? dateFormat(study.date, 'MMM d, yyyy', { locale: getDateLocale(i18n.language) })
                                        : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    <ActionButton variant="outlined" size="small" onClick={() => viewStudy(study.id)} startIcon={<VisibilityIcon />}>
                                        {t('admin_view')}
                                    </ActionButton>
                                    <ActionButton variant="contained" color="primary" size="small" disabled={saving} onClick={() => handleConfirm('approve', study)} startIcon={<CheckCircleOutlineIcon />}>
                                        {t('admin_approve')}
                                    </ActionButton>
                                    <ActionButton variant="outlined" color="secondary" size="small" disabled={saving} onClick={() => handleConfirm('reject', study)} startIcon={<HighlightOffIcon />}>
                                        {t('admin_reject')}
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            <Dialog open={confirmDialog.open} onClose={handleClose} aria-labelledby="confirm-dialog-title">
                <DialogTitle id="confirm-dialog-title">
                    {confirmDialog.action === 'approve' ? t('admin_confirmApproveTitle') : t('admin_confirmRejectTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.action === 'approve'
                            ? t('admin_confirmApproveMessage', { study: confirmDialog.study?.title })
                            : t('admin_confirmRejectMessage', { study: confirmDialog.study?.title })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="default">{t('admin_cancel')}</Button>
                    <Button onClick={handleAction} color={confirmDialog.action === 'approve' ? 'primary' : 'secondary'} variant="contained" autoFocus>
                        {confirmDialog.action === 'approve' ? t('admin_approve') : t('admin_reject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
